import {
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  faClone,
  faPen,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';

import { useParams } from 'react-router-dom';
import {
  GetProjectSettingsDocument,
  UpdateProjectDocument,
} from '../../../gql/graphql';
import SparkelIcon from '../../common/icon/SparkelIcon';
import CloneProjectTab from '../CloneProjectTab';
import CreateTemplateFromProjectTab from '../CreateTemplateFromProjectTab';
import DeleteProjectTab from '../DeleteProjectTab';
import ElementCategoriesTab from '../ElementCategories';
import ExportProjectTab from '../ExportProjectTab';
import OrderLabelsTab from '../OrderLabelsTab';
import TranslationTypeTab from '../TranslationType';

type ProjectSettingsModalProps = {
  projectId: string;
  modalIsOpen: boolean;
  closeModal: () => void;
};

type ProjectPathParams = {
  projectId: string;
};

export default function ProjectSettingsModal({
  modalIsOpen,
  closeModal,
  projectId,
}: ProjectSettingsModalProps): React.ReactElement {
  return (
    <Modal size={'4xl'} isOpen={modalIsOpen} onClose={closeModal}>
      <ModalOverlay></ModalOverlay>
      <ModalContent height={'60%'} minHeight={'fit-content'}>
        <ModalHeader paddingBottom={0}>Project settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {modalIsOpen ? (
            <ProjectSettings projectId={projectId} closeModal={closeModal} />
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function ProjectSettings({
  projectId,
  closeModal,
}: {
  projectId: string;
  closeModal: () => void;
}) {
  const [{ data: getProjectSettings, fetching, error }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId },
  });

  const params = useParams<keyof ProjectPathParams>() as ProjectPathParams;
  const isOnCompanyPage = params.projectId ? false : true;

  const project = getProjectSettings?.project;

  const { register, handleSubmit, reset } = useForm<{ name: string }>({
    defaultValues: { name: project?.name },
  });
  const toast = useToast();

  const [isEditing, setIsEditing] = useState(false);

  const [, updateProject] = useMutation(UpdateProjectDocument);

  const redIconColor = useColorModeValue('red.500', 'red.200');
  const cyanIconColor = useColorModeValue('cyan.500', 'cyan.300');

  const onRenameSubmit = handleSubmit(async ({ name }) => {
    if (project && name !== project.name) {
      const result = await updateProject({
        input: { id: projectId, patch: { name } },
      });

      if (result.error) {
        toast({
          title: 'An error occurred when renaming project',
          status: 'error',
        });
        throw Error();
      } else {
        toast({
          title: 'Project successfully renamed',
          status: 'success',
        });
        setIsEditing(false);
      }
    } else {
      setIsEditing(false);
    }
  });

  const dividerColor = useColorModeValue('gray.200', 'gray.700');

  if (fetching || !project) {
    return (
      <Center>
        <Spinner color="brand.400" />
      </Center>
    );
  }

  return (
    <>
      <Divider borderColor={dividerColor} marginBottom={2} />
      <Tabs
        orientation="vertical"
        variant="soft-rounded"
        colorScheme="blue"
        height={'100%'}
        size={'sm'}
      >
        <TabList width={300} marginRight={2}>
          <Tab justifyContent="left">Information</Tab>
          <Tab justifyContent="left">Table labels</Tab>
          <Tab justifyContent="left">Element attributes</Tab>
          <Tab justifyContent="left" isDisabled={isOnCompanyPage}>
            Export project
          </Tab>
          <Tab justifyContent="left">Advanced</Tab>
          <Divider borderColor={dividerColor} marginY={2} />
          <Tab
            justifyContent="left"
            padding={0}
            _selected={{ backgroundColor: 'none' }}
          >
            <Button
              size={'sm'}
              justifyContent={'left'}
              leftIcon={
                <SparkelIcon size="xs" color={cyanIconColor} icon={faClone} />
              }
              colorScheme="cyan"
              variant={'ghost'}
              width={'100%'}
            >
              Clone Project
            </Button>
          </Tab>
          <Tab
            justifyContent="left"
            padding={0}
            _selected={{ backgroundColor: 'none' }}
          >
            <Button
              size={'sm'}
              justifyContent={'left'}
              leftIcon={
                <SparkelIcon size="xs" color={cyanIconColor} icon={faPlus} />
              }
              colorScheme="cyan"
              variant={'ghost'}
              width={'100%'}
            >
              Save as template
            </Button>
          </Tab>
          <Tab
            justifyContent="left"
            padding={0}
            _selected={{ backgroundColor: 'none' }}
          >
            <Button
              size={'sm'}
              justifyContent={'left'}
              leftIcon={
                <SparkelIcon size="xs" color={redIconColor} icon={faTrash} />
              }
              colorScheme="red"
              variant={'ghost'}
              width={'100%'}
            >
              Delete project
            </Button>
          </Tab>
        </TabList>
        <Divider
          borderColor={dividerColor}
          orientation="vertical"
          height={'100%'}
        />
        <TabPanels padding={4}>
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size="md">
              Project Name
            </Heading>
            {isEditing ? (
              <form onSubmit={onRenameSubmit}>
                <Input
                  {...register('name', {
                    required: true,
                    onBlur: () => {
                      reset();
                      setIsEditing(false);
                    },
                  })}
                  py={1}
                  height="auto"
                  autoFocus={true}
                />
              </form>
            ) : (
              <Flex justifyContent={'flex-start'} py={1}>
                <Text>{project.name}</Text>
                <IconButton
                  ml="auto"
                  aria-label="Rename project"
                  colorScheme="gray"
                  size="xs"
                  onClick={() => setIsEditing(true)}
                  isRound
                  icon={<SparkelIcon icon={faPen} />}
                ></IconButton>
              </Flex>
            )}
          </TabPanel>
          <TabPanel paddingTop={0}>
            <OrderLabelsTab project={project}></OrderLabelsTab>
          </TabPanel>
          <TabPanel paddingTop={0}>
            <ElementCategoriesTab project={project}></ElementCategoriesTab>
          </TabPanel>
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size={'md'}>
              Export project
            </Heading>
            <ExportProjectTab />
          </TabPanel>
          <TabPanel paddingTop={0}>
            <TranslationTypeTab project={project}></TranslationTypeTab>
          </TabPanel>
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size={'md'}>
              Clone Project
            </Heading>
            <CloneProjectTab
              project={project}
              closeModal={closeModal}
            ></CloneProjectTab>
          </TabPanel>
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size={'md'}>
              Generate template from project
            </Heading>
            <CreateTemplateFromProjectTab
              project={project}
            ></CreateTemplateFromProjectTab>
          </TabPanel>
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size={'md'}>
              Delete project
            </Heading>
            <DeleteProjectTab
              project={project}
              closeModal={closeModal}
            ></DeleteProjectTab>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
}
